import { today as todayIn, civilToInstant } from "@calendar/shared";
import { useEffect } from "react";
import { MarketPanel } from './views/MarketPanel.js';
import { useApp } from "./store.js";
import { monthTitle, n } from "./format.js";
import { ListView, MonthView, WeekView } from "./views/CalendarViews.js";
import { desktopSetTrayIcon, isDesktop, onDesktopGoToday } from "./desktop.js";
import { EventEditor } from "./views/EventEditor.js";
import { LinkScreen } from "./views/LinkScreen.js";
import { SearchPanel } from "./views/SearchPanel.js";
import { SettingsPanel } from "./views/SettingsPanel.js";
import { TopBar } from "./views/TopBar.js";
import { YearView } from "./views/YearView.js";

/** True while a form control holds the keyboard, so shortcuts do not fire in
 * an editor, a search box or one of the topbar selects. */
function isTyping(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  return tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || target.isContentEditable;
}

export function App() {
  const app = useApp();
  const { state, timeZone, calendar, lang, t } = app;
  const today = todayIn(calendar, timeZone, new Date());

  // Keyboard navigation: arrows step by the active view's increment, T jumps
  // to today, Escape closes whatever is open. Held modifiers and form fields
  // are deliberately ignored so typing and select scrolling stay normal.
  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if (isTyping(e.target) || e.defaultPrevented) return;
      const key = e.key.toLowerCase();
      if (!app.state.user) return;
      if (app.state.editing) {
        if (key === "escape") app.closeEditor();
        return;
      }
      if (key === "escape" && app.state.view === "settings") {
        app.setView("month");
        return;
      }
      if (e.altKey || e.ctrlKey || e.metaKey) return;
      switch (key) {
        case "t":
        case "f5":
          app.goToday();
          break;
        case "arrowleft":
          app.shift(-1);
          break;
        case "arrowright":
          app.shift(1);
          break;
        case "arrowup":
          app.stepDay(-1);
          break;
        case "arrowdown":
          app.stepDay(1);
          break;
        case "n":
          app.startNewEvent();
          break;
      }
    }
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [app]);

  // The tray menu's "Jump to today" routes straight back into the same action
  // the toolbar button and T key use.
  useEffect(() => onDesktopGoToday(() => app.goToday()), [app]);

  // In the desktop shell, paint a live tray badge: the accent tile from the
  // theme with today's number on it, so the icon keeps its own mini-calendar.
  useEffect(() => {
    if (!isDesktop || !state.user) return;
    const canvas = document.createElement("canvas");
    canvas.width = 32;
    canvas.height = 32;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const style = getComputedStyle(document.documentElement);
    const fill = (style.getPropertyValue("--accent") || "#5b5bd6").trim();
    const text = (style.getPropertyValue("--on-accent") || "#ffffff").trim();
    ctx.fillStyle = fill;
    ctx.fillRect(0, 0, 32, 32);
    ctx.fillStyle = text;
    ctx.font = `bold 16px "Segoe UI", Tahoma, sans-serif`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(n(today.day, lang), 16, 17);
    desktopSetTrayIcon(canvas.toDataURL("image/png"));
  }, [today.day, lang, state.user]);

  // The toast host has to exist on the pairing screen as well. That screen is
  // exactly where a failed link has to be reported, so rendering it only after
  // sign-in means the error is raised with nowhere to appear.
  const toasts = (
    <div className="toasts">
      {state.toasts.map((toast) => (
        <div key={toast.id} className={`toast toast--${toast.kind}`}>
          {t(toast.text, toast.vars)}
        </div>
      ))}
    </div>
  );

  if (!state.ready) {
    return <div className="center muted">…</div>;
  }

  if (!state.user) {
    return (
      <>
        <LinkScreen lang={lang} t={t} onLink={app.linkAccount} toast={app.toast} />
        {toasts}
      </>
    );
  }

  const showGrid =
    state.view === "month" || state.view === "week" || state.view === "list" || state.view === "year";
  const shared = {
    days: app.visibleDays,
    events: state.events,
    anchor: state.anchor,
    today,
    timeZone,
    calendar,
    lang,
    t,
    onOpen: app.openEvent,
    onCreate: app.startNewEvent,
  };

  return (
    <div className="app">
      <TopBar
        displayName={state.user.displayName}
        year={state.anchor.year}
        years={app.yearOptions}
        onYear={app.setYear}
        view={state.view}
        timeZone={timeZone}
        calendar={calendar}
        lang={lang}
        t={t}
        onView={app.setView}
        onSave={app.updateUser}
        onOpenSettings={() => app.setView("settings")}
        onSignOut={app.signOut}
      />

      {showGrid && (
        <div className="toolbar">
          <button className="icon-btn" onClick={() => app.shift(-1)} aria-label={t("action.prev")}>
            ‹
          </button>
          <button className="btn" onClick={app.goToday}>
            {t("nav.today")}
          </button>
          <button className="icon-btn" onClick={() => app.shift(1)} aria-label={t("action.next")}>
            ›
          </button>
          <span className="toolbar__spacer" />
          {state.view === "year" ? (
            <span className="toolbar__title">{n(state.anchor.year,lang)}</span>
          ) : (
            <span className="toolbar__title">{monthTitle(state.anchor, calendar, lang)}</span>
          )}
          {state.loading && <span className="muted">…</span>}
          <span className="muted">{n(state.events.length, lang)}</span>
          <button className="btn btn--primary" onClick={() => app.startNewEvent()}>
            + {t("action.new")}
          </button>
        </div>
      )}

      <main className="board">
        {showGrid && <MarketPanel lang={lang} selectedDay={new Intl.DateTimeFormat('en-CA',{timeZone:'Asia/Tehran',year:'numeric',month:'2-digit',day:'2-digit'}).format(new Date(civilToInstant(state.anchor,12,0,timeZone,calendar)))} />}
        {state.view === "year" && (
          <YearView
            year={state.anchor.year}
            calendar={calendar}
            lang={lang}
            t={t}
            eventCounts={app.eventCounts}
            onOpenDay={(day) => {
              app.setAnchor(day);
              app.setView("month");
            }}
          />
        )}
        {state.view === "month" && <MonthView {...shared} />}
        {state.view === "week" && <WeekView {...shared} />}
        {state.view === "list" && <ListView {...shared} />}
        {state.view === "search" && (
          <SearchPanel
            results={state.noteResults}
            query={state.query}
            timeZone={timeZone}
            calendar={calendar}
            lang={lang}
            t={t}
            onSearch={(q) => void app.searchNotes(q)}
            onOpen={app.openEvent}
          />
        )}
        {state.view === "settings" && <SettingsPanel app={app} />}
      </main>

      {toasts}

      {state.editing && (
        <EventEditor
          event={state.editing}
          seed={state.editorSeed}
          timeZone={timeZone}
          calendar={calendar}
          lang={lang}
          categories={state.categories}
          t={t}
          toast={app.toast}
          onClose={app.closeEditor}
          onSave={app.saveEvent}
          onDelete={app.removeEvent}
          onAttach={app.attachFile}
          onSetReminder={app.setReminder}
        />
      )}
    </div>
  );
}
